import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  Loader2,
  Lock,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { autoriserClotureManuelle, cloturerExercice, fetchExerciceChiffres } from '../services/api';

const euros = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/**
 * Exercice comptable en tête du tableau de bord : le chiffre de l'année
 * courante, celui de la précédente pour comparer, et la clôture.
 *
 * La clôture archive l'année et ouvre la suivante. Elle est irréversible :
 * elle demande donc une confirmation explicite, et n'est proposée qu'au super
 * administrateur et à un administrateur président.
 */
export default function CarteExercice() {
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bureau-exercice-chiffres'],
    queryFn: fetchExerciceChiffres,
  });

  const cloture = useMutation({
    mutationFn: cloturerExercice,
    onSuccess: (d) => {
      setNotice({ type: 'success', message: d.message });
      setConfirmation(false);
      // Tout ce qui porte une notion d'exercice devient périmé d'un coup.
      queryClient.invalidateQueries({ queryKey: ['bureau-exercice-chiffres'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-exercice-archives'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-sponsors'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-tableau-de-bord'] });
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  const verrou = useMutation({
    mutationFn: (autorisee: boolean) => autoriserClotureManuelle(autorisee),
    onSuccess: (d: any) => {
      setNotice({ type: 'success', message: d.message });
      queryClient.invalidateQueries({ queryKey: ['bureau-exercice-chiffres'] });
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  if (isLoading || !data) {
    return (
      <div className="card flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const { anneeN, anneeN1, chiffreN, chiffreN1, souscritN, variation, peutCloturer } = data;
  // Le bouton dit ce qu'il ferme et quand cela tombera tout seul. Il ne
  // répond au clic que si la clôture à la main a été ouverte : elle est
  // irréversible, elle déplace une année entière, et le régime automatique la
  // rend inutile dans la vie courante — la laisser à portée de clic, c'est
  // attendre le jour où quelqu'un clôture l'année en cours au mois de mars.
  const clotureManuelle = data.cloture?.manuelleAutorisee ?? false;
  const libelleCloture = data.cloture?.libelle ?? `Clôturer l'exercice ${anneeN}`;
  const resteN = Math.max(0, souscritN - chiffreN);
  const enHausse = variation !== null && variation >= 0;

  return (
    <section className="card lg:p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Exercice en cours</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">{anneeN}</h2>
        </div>
        <Link
          to="/bureau/archives"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400"
        >
          <Archive className="w-4 h-4" /> Archives
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl bg-primary-50 dark:bg-primary-900/20 p-4">
          <p className="text-xs text-primary-700 dark:text-primary-300">Encaissé {anneeN}</p>
          <p className="text-2xl font-bold text-primary-800 dark:text-primary-200 tabular-nums">
            {euros.format(chiffreN)}
          </p>
          {/* Le total additionne parrainages, dons et billetterie : le détail
              évite de chercher pourquoi il dépasse la somme des factures. */}
          {((data.donsN ?? 0) > 0 || (data.billetterieN ?? 0) > 0) && (
            <p className="text-xs text-primary-700/80 dark:text-primary-300/80 mt-0.5">
              dont {euros.format(data.donsN ?? 0)} de dons et{' '}
              {euros.format(data.billetterieN ?? 0)} de billetterie
            </p>
          )}
          {resteN > 0 && (
            <p className="text-xs text-amber-600 mt-0.5">
              reste {euros.format(resteN)} à encaisser
            </p>
          )}
        </div>

        <div className="rounded-xl bg-gray-50 dark:bg-gray-800 p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Encaissé {anneeN1}</p>
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-200 tabular-nums">
            {euros.format(chiffreN1)}
          </p>
          {!data.exercicePrecedentConnu && chiffreN1 === 0 && (
            <p className="text-xs text-gray-400 mt-0.5">premier exercice</p>
          )}
        </div>

        <div className="rounded-xl bg-gray-50 dark:bg-gray-800 p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Évolution</p>
          {variation === null ? (
            <p className="text-2xl font-bold text-gray-300">—</p>
          ) : (
            <p
              className={`text-2xl font-bold tabular-nums flex items-center gap-1.5 ${
                enHausse ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {enHausse ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              {enHausse ? '+' : ''}
              {variation.toLocaleString('fr-FR')} %
            </p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">par rapport à {anneeN1}</p>
        </div>
      </div>

      {notice && (
        <p
          className={`text-sm rounded-xl px-3 py-2 ${
            notice.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {notice.message}
        </p>
      )}

      {peutCloturer && (
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
          {confirmation ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  L'exercice <strong>{anneeN}</strong> sera archivé et l'exercice{' '}
                  <strong>{anneeN + 1}</strong> ouvert. Les documents déjà émis restent
                  consultables dans les archives, mais <strong>l'opération est définitive</strong>.
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => cloture.mutate()}
                  disabled={cloture.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50"
                >
                  {cloture.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirmer la clôture de {anneeN}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmation(false)}
                  disabled={cloture.isPending}
                  className="btn-outline text-sm py-2 px-4 disabled:opacity-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : clotureManuelle ? (
            <button
              type="button"
              onClick={() => {
                setNotice(null);
                setConfirmation(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-amber-300 text-amber-700 hover:bg-amber-50 font-medium"
            >
              <Lock className="w-4 h-4" />
              {libelleCloture}
            </button>
          ) : (
            <div className="space-y-1.5">
              <button
                type="button"
                disabled
                title="La clôture à la main est fermée sur cet espace."
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 font-medium cursor-not-allowed"
              >
                <Lock className="w-4 h-4" />
                {libelleCloture}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {data.cloture?.mode === 'desactivee'
                  ? 'La clôture à la main est fermée sur cet espace.'
                  : 'Votre exercice se clôture tout seul à cette date.'}{' '}
                Pour clôturer avant terme, écrivez à{' '}
                <a className="underline" href="mailto:assistance@gest-asso.com">assistance@gest-asso.com</a>.
              </p>
              {/* Le verrou s'ouvre depuis ici, et seulement pour l'éditeur :
                  c'est la réponse à un courrier, pas une case de plus dans
                  les paramètres du client. */}
              {data.estSuperAdmin && (
                <button
                  type="button"
                  onClick={() => verrou.mutate(true)}
                  disabled={verrou.isPending}
                  className="text-xs text-primary-700 dark:text-primary-300 hover:underline disabled:opacity-50"
                >
                  Ouvrir la clôture manuelle sur cet espace
                </button>
              )}
            </div>
          )}

          {data.estSuperAdmin && clotureManuelle && (
            <button
              type="button"
              onClick={() => verrou.mutate(false)}
              disabled={verrou.isPending}
              className="mt-2 block text-xs text-gray-500 hover:underline disabled:opacity-50"
            >
              Refermer la clôture manuelle
            </button>
          )}
        </div>
      )}
    </section>
  );
}
